import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, Mic, Video, Type, Square, RotateCcw, Send, Pencil, Trash2, Check, X, Play, Upload } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { useMediaRecording, type MediaRecordingResult } from '@/lib/voice/use-media-recording';
import { Mp3Encoder } from '@breezystack/lamejs';
import { cn } from './ui/utils';

const BUCKET = 'pre-meeting-updates';
const MAX_SECONDS = 300;
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // must match the project-wide Supabase storage upload limit (100 MiB)
const MAX_UPLOAD_SECONDS = 30 * 60; // uploads can be longer than a live recording (which stays at MAX_SECONDS)
// In-meeting playback runs on Recall's CPU-only bot browser, which can't software-decode high-res /
// high-fps video in real time (a 4K50 upload just renders black and never starts). Anything larger than
// this box is re-encoded client-side down to a bot-safe 720p30 WebM before upload - see transcodeVideoForBot.
const BOT_MAX_WIDTH = 1280;
const BOT_MAX_HEIGHT = 720;
const BOT_TARGET_FPS = 30;
const BOT_VIDEO_BITRATE = 3_000_000;

export type CheckInContentType = 'text' | 'audio' | 'video';
/** Recorder UI mode. 'upload' isn't a content type - it produces a video check-in from a picked file. */
type RecorderMode = CheckInContentType | 'upload';

export interface CheckInRow {
  id: string;
  user_id: string;
  body_text?: string | null;
  content_type?: CheckInContentType | null;
  media_path?: string | null;
  media_mime?: string | null;
  transcript_text?: string | null;
  transcript_status?: string | null;
  submitted_at?: string | null;
}

interface CheckInRecorderProps {
  meetingId: string;
  userId: string;
  /** Called after a new update is appended, so the caller can refresh the submissions list. */
  onSubmitted?: () => void | Promise<void>;
  disabled?: boolean;
}

function extFromMime(mime: string): string {
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('quicktime')) return 'mov';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mpeg')) return 'mp3';
  return 'webm';
}

/** Infer a browser-playable video MIME from a filename - some OSes hand File.type back empty for
 *  otherwise-fine videos, which would wrongly fail the "is this a video?" check. Returns '' if unknown. */
function mimeFromVideoExt(name: string): string {
  const ext = name.toLowerCase().split('.').pop() || '';
  if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'webm') return 'video/webm';
  if (ext === 'ogv') return 'video/ogg';
  return '';
}

/** Read an uploaded video's intrinsic size + duration. Dimensions are known at loadedmetadata; duration
 *  may need a seek-to-end for no-duration containers. Resolves null only if the browser can't decode the
 *  file at all (unsupported/corrupt codec). */
function readVideoMeta(
  file: Blob,
): Promise<{ width: number; height: number; durationSeconds: number | null } | null> {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    const url = URL.createObjectURL(file);
    let settled = false;
    const finish = (v: { width: number; height: number; durationSeconds: number | null } | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(v);
    };
    el.onloadedmetadata = () => {
      const width = el.videoWidth || 0;
      const height = el.videoHeight || 0;
      if (Number.isFinite(el.duration) && el.duration > 0) {
        return finish({ width, height, durationSeconds: el.duration });
      }
      el.onseeked = () =>
        finish({
          width,
          height,
          durationSeconds: Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null,
        });
      try {
        el.currentTime = 1e101;
      } catch {
        finish({ width, height, durationSeconds: null });
      }
    };
    el.onerror = () => finish(null);
    el.src = url;
  });
}

/** MediaRecorder container/codec for the transcode output - same candidates the live recorder uses, so the
 *  bot plays it via the exact path proven to work for recordings (VP8/VP9 WebM). */
function pickTranscodeMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const c of ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

/**
 * Downscale + re-encode an uploaded video to a bot-safe rendition entirely in the browser: play it into a
 * hidden <video>, draw each frame onto a canvas scaled to fit within BOT_MAX_WIDTH×BOT_MAX_HEIGHT, and
 * capture that canvas (plus the file's audio, routed silently through WebAudio) with MediaRecorder. The
 * user's machine has a real GPU so it can decode the 4K source the bot can't; the output is a small 720p30
 * WebM the bot's CPU decoder handles trivially. Runs in real time (paced by playback, so audio stays in
 * sync). Returns null if the browser can't decode the source or lacks MediaRecorder.
 */
async function transcodeVideoForBot(
  file: Blob,
  onProgress?: (fraction: number) => void,
): Promise<MediaRecordingResult | null> {
  if (typeof document === 'undefined') return null;
  const mimeType = pickTranscodeMimeType();
  if (!mimeType) return null;

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.src = url;
  video.playsInline = true;
  video.preload = 'auto';

  let audioCtx: AudioContext | null = null;
  const cleanup = () => {
    try {
      video.pause();
    } catch {
      /* ignore */
    }
    try {
      audioCtx?.close();
    } catch {
      /* ignore */
    }
    video.removeAttribute('src');
    try {
      video.load();
    } catch {
      /* ignore */
    }
    URL.revokeObjectURL(url);
  };

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('decode failed'));
    });

    const sw = video.videoWidth;
    const sh = video.videoHeight;
    if (!sw || !sh) return null;

    const scale = Math.min(BOT_MAX_WIDTH / sw, BOT_MAX_HEIGHT / sh, 1);
    const tw = Math.max(2, Math.round((sw * scale) / 2) * 2);
    const th = Math.max(2, Math.round((sh * scale) / 2) * 2);

    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const stream = canvas.captureStream(BOT_TARGET_FPS);

    // Tap the file's audio through WebAudio into the recording stream. Connecting only to the stream
    // destination (never ctx.destination) captures the track without playing it out of the speakers.
    try {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor) {
        audioCtx = new Ctor();
        const source = audioCtx.createMediaElementSource(video);
        const dest = audioCtx.createMediaStreamDestination();
        source.connect(dest);
        const track = dest.stream.getAudioTracks()[0];
        if (track) stream.addTrack(track);
        if (audioCtx.state === 'suspended') await audioCtx.resume().catch(() => {});
      }
    } catch {
      // No audio track (source has none, or the tap failed) - video-only transcode still plays.
      video.muted = true;
    }

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: BOT_VIDEO_BITRATE });
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data);
    };
    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });

    const durSec = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
    let rafId = 0;
    const draw = () => {
      ctx.drawImage(video, 0, 0, tw, th);
      if (durSec && onProgress) onProgress(Math.min(1, video.currentTime / durSec));
      rafId = requestAnimationFrame(draw);
    };

    recorder.start(1000);
    await video.play(); // rejects if unmuted autoplay is blocked → caught below → null
    draw();

    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      video.onended = finish;
      // Safety net if 'ended' never fires (stalled decode): cap at clip duration + margin.
      const capMs = (durSec > 0 ? durSec * 1000 : MAX_UPLOAD_SECONDS * 1000) + 5000;
      window.setTimeout(finish, capMs);
    });

    cancelAnimationFrame(rafId);
    try {
      ctx.drawImage(video, 0, 0, tw, th);
    } catch {
      /* ignore */
    }
    if (recorder.state !== 'inactive') recorder.stop();
    await stopped;

    const blob = new Blob(chunks, { type: mimeType });
    if (!blob.size) return null;
    return { blob, mimeType, durationSeconds: durSec ? Math.round(durSec) : 0, kind: 'video' };
  } catch {
    return null;
  } finally {
    cleanup();
  }
}

function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

async function createCheckInSignedUrl(path: string): Promise<string | null> {
  const { data } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
  return data?.signedUrl ?? null;
}

/**
 * MediaRecorder writes webm/mp4 with no duration in the container header, so `el.duration` comes back
 * as Infinity. The native seek bar then can't track progress and jumps backwards when you pause. The
 * standard workaround: once metadata loads, seek to the far end to force the browser to scan and
 * resolve the real duration, then snap back to the start. Runs once per element.
 */
function resolveMediaDuration(el: HTMLMediaElement) {
  if (Number.isFinite(el.duration)) return;
  const reset = () => {
    el.removeEventListener('timeupdate', reset);
    el.currentTime = 0;
  };
  el.addEventListener('timeupdate', reset);
  try {
    el.currentTime = 1e101;
  } catch {
    el.removeEventListener('timeupdate', reset);
  }
}

/**
 * Encode the audio track of a recording to a mono ~32 kbps MP3 for in-call playback - Recall's
 * output_audio only accepts MP3, and 32 kbps mono keeps a full 5-min clip under its size cap.
 * Returns null on any failure; the update still saves and the bot falls back to the transcript.
 */
async function encodeBlobToMonoMp3(blob: Blob): Promise<Blob | null> {
  try {
    const arrayBuf = await blob.arrayBuffer();
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AC();
    let audioBuf: AudioBuffer;
    try {
      audioBuf = await ctx.decodeAudioData(arrayBuf.slice(0));
    } finally {
      void ctx.close();
    }
    const channels = audioBuf.numberOfChannels;
    const len = audioBuf.length;
    if (len === 0) return null;
    // Downmix to mono.
    const mono = new Float32Array(len);
    for (let c = 0; c < channels; c++) {
      const data = audioBuf.getChannelData(c);
      for (let i = 0; i < len; i++) mono[i] += data[i] / channels;
    }
    // Float32 [-1,1] → Int16 PCM.
    const int16 = new Int16Array(len);
    for (let i = 0; i < len; i++) {
      const s = Math.max(-1, Math.min(1, mono[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    const encoder = new Mp3Encoder(1, Math.round(audioBuf.sampleRate), 32);
    const blockSize = 1152;
    const parts: Uint8Array[] = [];
    for (let i = 0; i < int16.length; i += blockSize) {
      const buf = encoder.encodeBuffer(int16.subarray(i, i + blockSize));
      if (buf.length > 0) parts.push(buf);
    }
    const tail = encoder.flush();
    if (tail.length > 0) parts.push(tail);
    if (parts.length === 0) return null;
    return new Blob(parts as BlobPart[], { type: 'audio/mpeg' });
  } catch {
    return null;
  }
}

/**
 * Playback + transcript for a submitted audio/video check-in. Mints a short-lived signed URL
 * client-side (allowed by the meeting-scoped Storage SELECT policy).
 */
export function CheckInMedia({
  contentType,
  mediaPath,
  transcriptText,
  transcriptStatus,
  compact,
}: {
  contentType?: CheckInContentType | null;
  mediaPath?: string | null;
  transcriptText?: string | null;
  transcriptStatus?: string | null;
  compact?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const isMedia = (contentType === 'audio' || contentType === 'video') && !!mediaPath;

  useEffect(() => {
    let active = true;
    if (isMedia && mediaPath) {
      void createCheckInSignedUrl(mediaPath).then((u) => {
        if (active) setUrl(u);
      });
    } else {
      setUrl(null);
    }
    return () => {
      active = false;
    };
  }, [isMedia, mediaPath]);

  if (!isMedia) return null;

  return (
    <div className="space-y-2">
      {url ? (
        contentType === 'video' ? (
          <video
            src={url}
            controls
            onLoadedMetadata={(e) => resolveMediaDuration(e.currentTarget)}
            className={cn('w-full rounded-md bg-black', compact ? 'max-h-48' : 'max-h-72')}
          />
        ) : (
          <audio src={url} controls onLoadedMetadata={(e) => resolveMediaDuration(e.currentTarget)} className="w-full" />
        )
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading recording…
        </div>
      )}
      {transcriptStatus === 'ready' && transcriptText ? (
        <p className="whitespace-pre-wrap text-sm text-foreground">{transcriptText}</p>
      ) : transcriptStatus === 'pending' || transcriptStatus === 'processing' ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Transcribing…
        </p>
      ) : transcriptStatus === 'failed' ? (
        <p className="text-xs text-muted-foreground">Transcription unavailable.</p>
      ) : null}
    </div>
  );
}

const TYPE_ICON: Record<CheckInContentType, typeof Type> = {
  text: Type,
  audio: Mic,
  video: Video,
};

/**
 * Audit-log list of all submitted updates for a meeting, newest first. Reused across the
 * attendee portal, dashboard, and meetings views.
 */
export function CheckInList({
  items,
  authorLabel,
  emptyText = 'No updates yet.',
  currentUserId,
  canManage = false,
  onChanged,
  canPlayInMeeting = false,
  onPlay,
  playingId = null,
}: {
  items: CheckInRow[];
  authorLabel?: (userId: string) => string;
  emptyText?: string;
  /** The viewer's user id - enables Edit + Delete on their own updates. */
  currentUserId?: string;
  /** Meeting owner - may delete anyone's update (text editing stays author-only per RLS). */
  canManage?: boolean;
  /** Called after an edit or delete so the caller can refresh its list. */
  onChanged?: () => void | Promise<void>;
  /** When true + onPlay set, show a per-item "Play in meeting" button (owner, bot live). */
  canPlayInMeeting?: boolean;
  onPlay?: (checkInId: string) => void | Promise<void>;
  /** The check-in currently being played (spinner + disable). */
  playingId?: string | null;
}) {
  const sorted = useMemo(
    () => [...items].sort((a, b) => (b.submitted_at ?? '').localeCompare(a.submitted_at ?? '')),
    [items],
  );
  // Canonical 1-based number per check-in, in submission order (submitted_at, then id) - matches
  // play-check-ins' ordering so the "#N" badge lines up with "@upstand play N".
  const numberById = useMemo(() => {
    const asc = [...items].sort(
      (a, b) => (a.submitted_at ?? '').localeCompare(b.submitted_at ?? '') || a.id.localeCompare(b.id),
    );
    const m = new Map<string, number>();
    asc.forEach((c, i) => m.set(c.id, i + 1));
    return m;
  }, [items]);

  if (sorted.length === 0) {
    return <p className="text-sm italic text-muted-foreground">{emptyText}</p>;
  }

  return (
    <ul className="space-y-2">
      {sorted.map((c) => (
        <CheckInListItem
          key={c.id}
          item={c}
          number={numberById.get(c.id) ?? null}
          authorLabel={authorLabel}
          currentUserId={currentUserId}
          canManage={canManage}
          onChanged={onChanged}
          canPlayInMeeting={canPlayInMeeting}
          onPlay={onPlay}
          playing={playingId === c.id}
        />
      ))}
    </ul>
  );
}

function CheckInListItem({
  item: c,
  number,
  authorLabel,
  currentUserId,
  canManage,
  onChanged,
  canPlayInMeeting,
  onPlay,
  playing,
}: {
  item: CheckInRow;
  number?: number | null;
  authorLabel?: (userId: string) => string;
  currentUserId?: string;
  canManage?: boolean;
  onChanged?: () => void | Promise<void>;
  canPlayInMeeting?: boolean;
  onPlay?: (checkInId: string) => void | Promise<void>;
  playing?: boolean;
}) {
  const kind = (c.content_type as CheckInContentType) || 'text';
  const Icon = TYPE_ICON[kind] ?? Type;
  const isAuthor = !!currentUserId && currentUserId === c.user_id;
  const canEdit = isAuthor; // RLS allows updating only your own row
  const canDelete = isAuthor || !!canManage;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body_text ?? '');
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { error } = await supabase
        .from('pre_meeting_check_ins')
        .update({ body_text: draft.trim() })
        .eq('id', c.id);
      if (error) throw error;
      setEditing(false);
      await onChanged?.();
      toast.success('Update edited');
    } catch (e: any) {
      toast.error(e?.message || 'Could not save edit');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await invokeEdgeFn('delete-check-in', { check_in_id: c.id });
      setConfirmOpen(false);
      await onChanged?.();
      toast.success('Update deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="space-y-2 rounded-md border border-border bg-card px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {number != null && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-foreground/70">#{number}</span>
          )}
          <Icon className="h-3.5 w-3.5" />
          {authorLabel ? authorLabel(c.user_id) : kind}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">
            {c.submitted_at
              ? new Date(c.submitted_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
              : ''}
          </span>
          {canPlayInMeeting && onPlay && (
            <button
              type="button"
              onClick={() => void onPlay(c.id)}
              disabled={playing}
              title="Play in meeting"
              className="rounded p-1 text-muted-foreground transition-colors hover:text-primary disabled:opacity-50"
            >
              {playing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            </button>
          )}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => {
                setDraft(c.body_text ?? '');
                setEditing(true);
              }}
              title="Edit"
              className="rounded p-1 text-muted-foreground transition-colors hover:text-primary"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
              title="Delete"
              className="rounded p-1 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <CheckInMedia
        contentType={c.content_type}
        mediaPath={c.media_path}
        transcriptText={c.transcript_text}
        transcriptStatus={c.transcript_status}
        compact
      />

      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            disabled={busy}
            placeholder={kind === 'text' ? 'Your update…' : 'Add a note…'}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void saveEdit()} disabled={busy}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="mr-1 h-4 w-4" /> Save
                </>
              )}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              <X className="mr-1 h-4 w-4" /> Cancel
            </Button>
          </div>
        </div>
      ) : c.body_text ? (
        <p className="whitespace-pre-wrap text-foreground/90">{c.body_text}</p>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this update?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the update{kind !== 'text' ? ' and its recording' : ''}. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void doDelete();
              }}
              disabled={busy}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

const MODES: { value: RecorderMode; label: string; icon: typeof Type }[] = [
  { value: 'text', label: 'Type', icon: Type },
  { value: 'audio', label: 'Record audio', icon: Mic },
  { value: 'video', label: 'Record video', icon: Video },
  { value: 'upload', label: 'Upload video', icon: Upload },
];

/**
 * Add a pre-meeting update as text, voice, or video. Each submission is appended as its own row
 * (audit-log model). Owns the full write path: Storage upload + insert + transcription trigger.
 */
export function CheckInRecorder({ meetingId, userId, onSubmitted, disabled }: CheckInRecorderProps) {
  const [mode, setMode] = useState<RecorderMode>('text');
  const [text, setText] = useState('');
  const [caption, setCaption] = useState('');
  const [recorded, setRecorded] = useState<MediaRecordingResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleComplete = useCallback((result: MediaRecordingResult) => setRecorded(result), []);
  const rec = useMediaRecording({
    kind: mode === 'video' ? 'video' : 'audio',
    maxSeconds: MAX_SECONDS,
    onComplete: handleComplete,
  });

  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = videoPreviewRef.current;
    if (!el) return;
    if (rec.previewStream) {
      el.srcObject = rec.previewStream;
      void el.play?.().catch(() => {});
    } else {
      el.srcObject = null;
    }
  }, [rec.previewStream]);

  const recordedUrl = useMemo(() => (recorded ? URL.createObjectURL(recorded.blob) : null), [recorded]);
  useEffect(() => {
    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  const busy = submitting || disabled || uploadStatus !== null;

  const switchMode = (next: RecorderMode) => {
    if (rec.recording) rec.cancel();
    setRecorded(null);
    setUploadError(null);
    setUploadStatus(null);
    setUploadProgress(null);
    setMode(next);
  };

  /** Validate a picked video file, then stage it through the same path a recording uses (preview,
   *  caption, submitMedia). If it's higher-res than the bot can decode in real time, downscale it to a
   *  bot-safe 720p30 rendition in the browser first - otherwise it plays as a black screen in-meeting. */
  const handleFilePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;
    // Accept by MIME, falling back to the extension when the OS reports an empty File.type.
    const mimeType = file.type.startsWith('video/') ? file.type : mimeFromVideoExt(file.name);
    if (!mimeType) {
      setUploadError('Please choose a video file (MP4, MOV, WebM, or M4V).');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`That file is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB).`);
      return;
    }

    setUploadError(null);
    setUploadProgress(null);
    setUploadStatus('Checking video…');
    const meta = await readVideoMeta(file);
    if (!meta) {
      setUploadStatus(null);
      setUploadError('We couldn’t read that video. Please upload an MP4 (H.264), MOV, or WebM.');
      return;
    }
    if (meta.durationSeconds != null && meta.durationSeconds > MAX_UPLOAD_SECONDS + 1) {
      setUploadStatus(null);
      setUploadError(`That video is too long (max ${Math.round(MAX_UPLOAD_SECONDS / 60)} minutes).`);
      return;
    }

    // Recall's CPU-only bot can't decode >720p in real time - downscale before upload or it plays black.
    if (meta.width > BOT_MAX_WIDTH || meta.height > BOT_MAX_HEIGHT) {
      setUploadStatus('Optimising video for in-meeting playback…');
      const optimised = await transcodeVideoForBot(file, (f) => setUploadProgress(f));
      setUploadProgress(null);
      setUploadStatus(null);
      if (!optimised) {
        setUploadError(
          'This video is too high-resolution to play in meetings and couldn’t be optimised in this browser. ' +
            'Try a 1080p-or-lower video, or use Chrome, Edge, or Firefox.',
        );
        return;
      }
      setRecorded(optimised);
      return;
    }

    setUploadStatus(null);
    setRecorded({
      blob: file,
      mimeType,
      durationSeconds: meta.durationSeconds ? Math.round(meta.durationSeconds) : 0,
      kind: 'video',
    });
  };

  const submitText = async () => {
    if (!text.trim()) {
      toast.error('Please enter your update');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('pre_meeting_check_ins').insert({
        meeting_id: meetingId,
        user_id: userId,
        body_text: text.trim(),
        content_type: 'text',
        transcript_status: 'none',
        submitted_at: new Date().toISOString(),
      });
      if (error) throw error;
      toast.success('Update added');
      setText('');
      await onSubmitted?.();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add update');
    } finally {
      setSubmitting(false);
    }
  };

  const submitMedia = async () => {
    if (!recorded) return;
    setSubmitting(true);
    try {
      const baseMime = recorded.mimeType.split(';')[0];
      const uid = crypto.randomUUID();
      const path = `${meetingId}/${userId}/${uid}.${extFromMime(recorded.mimeType)}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, recorded.blob, { contentType: baseMime, upsert: false });
      if (upErr) throw upErr;

      // Encode + upload a mono MP3 rendition for in-call playback. Best-effort: on failure the update
      // still saves (audio_mp3_path stays null) and the bot falls back to the transcript when playing.
      let audioMp3Path: string | null = null;
      const mp3 = await encodeBlobToMonoMp3(recorded.blob);
      if (mp3) {
        const mp3Path = `${meetingId}/${userId}/${uid}.mp3`;
        const { error: mp3Err } = await supabase.storage
          .from(BUCKET)
          .upload(mp3Path, mp3, { contentType: 'audio/mpeg', upsert: false });
        if (!mp3Err) audioMp3Path = mp3Path;
      }

      const { data: inserted, error: rowErr } = await supabase
        .from('pre_meeting_check_ins')
        .insert({
          meeting_id: meetingId,
          user_id: userId,
          body_text: caption.trim(),
          content_type: recorded.kind,
          media_path: path,
          media_mime: baseMime,
          media_duration_seconds: recorded.durationSeconds,
          media_bytes: recorded.blob.size,
          audio_mp3_path: audioMp3Path,
          transcript_status: 'pending',
          submitted_at: new Date().toISOString(),
        })
        .select('id')
        .single();
      if (rowErr) throw rowErr;

      // Kick off transcription for this specific entry. Best-effort: the row stays 'pending' if it
      // fails, so a future sweep (or re-open) can retry - the recording is already safely stored.
      if (inserted?.id) {
        try {
          await invokeEdgeFn('transcribe-check-in', { check_in_id: inserted.id });
        } catch {
          /* noop - recording is saved; transcript fills in later */
        }
      }

      toast.success('Update added');
      setRecorded(null);
      setCaption('');
      await onSubmitted?.();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add update');
    } finally {
      setSubmitting(false);
    }
  };

  const recordModesAvailable = !rec.unsupported;

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
        {MODES.map((m) => {
          const Icon = m.icon;
          const needsRecorder = m.value === 'audio' || m.value === 'video';
          if (needsRecorder && !recordModesAvailable) return null;
          return (
            <button
              key={m.value}
              type="button"
              disabled={busy}
              onClick={() => switchMode(m.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                mode === m.value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {mode === 'text' && (
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share what you'd like the team to know - wins, blockers, status, etc."
            rows={4}
            disabled={busy}
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={submitText} disabled={busy || !text.trim()}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Submit
            </Button>
          </div>
        </div>
      )}

      {(mode === 'audio' || mode === 'video') && (
        <div className="space-y-3">
          {rec.unsupported ? (
            <p className="text-sm text-muted-foreground">
              Recording isn’t supported in this browser. Switch to “Type”, or try Chrome, Edge, or Firefox.
            </p>
          ) : (
            <>
              {mode === 'video' && rec.recording && (
                <video ref={videoPreviewRef} muted playsInline className="w-full max-h-72 rounded-md bg-black" />
              )}

              {recorded && recordedUrl && !rec.recording && (
                <>
                  {recorded.kind === 'video' ? (
                    <video src={recordedUrl} controls className="w-full max-h-72 rounded-md bg-black" />
                  ) : (
                    <audio src={recordedUrl} controls className="w-full" />
                  )}
                  <Textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Add a note to go with your recording (optional)"
                    rows={2}
                    disabled={busy}
                  />
                </>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {rec.recording ? (
                  <>
                    <Button size="sm" variant="destructive" onClick={rec.stop}>
                      <Square className="mr-2 h-4 w-4" /> Stop
                    </Button>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {formatClock(rec.elapsedSeconds)} / {formatClock(MAX_SECONDS)}
                    </span>
                  </>
                ) : recorded ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => { setRecorded(null); rec.start(); }} disabled={busy}>
                      <RotateCcw className="mr-2 h-4 w-4" /> Re-record
                    </Button>
                    <Button size="sm" onClick={submitMedia} disabled={busy}>
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      Submit {recorded.kind}
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={rec.start} disabled={busy}>
                    {mode === 'video' ? <Video className="mr-2 h-4 w-4" /> : <Mic className="mr-2 h-4 w-4" />}
                    Record {mode}
                  </Button>
                )}
              </div>

              {rec.errorMessage && <p className="text-sm text-destructive">{rec.errorMessage}</p>}
              {!rec.recording && !recorded && (
                <p className="text-xs text-muted-foreground">
                  Up to {Math.round(MAX_SECONDS / 60)} minutes. We’ll transcribe it automatically. Your{' '}
                  {mode === 'video' ? 'camera and microphone' : 'microphone'} is only used while recording.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {mode === 'upload' && (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={handleFilePick}
            disabled={busy}
          />
          {recorded && recordedUrl ? (
            <>
              <video src={recordedUrl} controls className="w-full max-h-72 rounded-md bg-black" />
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a note to go with your video (optional)"
                rows={2}
                disabled={busy}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setRecorded(null);
                    fileInputRef.current?.click();
                  }}
                  disabled={busy}
                >
                  <RotateCcw className="mr-2 h-4 w-4" /> Choose another
                </Button>
                <Button size="sm" onClick={submitMedia} disabled={busy}>
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Submit video
                </Button>
              </div>
            </>
          ) : uploadStatus ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                {uploadStatus}
                {uploadProgress != null ? ` ${Math.round(uploadProgress * 100)}%` : ''}
              </span>
            </div>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                <Upload className="mr-2 h-4 w-4" /> Choose a video file
              </Button>
              <p className="text-xs text-muted-foreground">
                MP4, WebM, or MOV, up to {Math.round(MAX_UPLOAD_SECONDS / 60)} minutes /{' '}
                {Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB. High-resolution videos (e.g. 4K phone
                recordings) are automatically optimised for in-meeting playback, then transcribed.
              </p>
            </>
          )}
          {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
        </div>
      )}
    </div>
  );
}
